/**
 * Cookie Management Module
 * Handles cookie consent, storage, and compliance
 */
import type { CookieConsent, CookieTypes } from '../types';

export class CookieManager {
    cookieConsent: CookieConsent;
    isDevelopment: boolean;
    cookieTypes: CookieTypes;

    constructor() {
        this.isDevelopment = this.checkIfDevelopment();
        this.cookieConsent = this.getCookieConsent();
        this.cookieTypes = {
            essential: {
                name: 'Essential Cookies',
                description: 'Required for basic website functionality',
                details: 'These cookies are necessary for the website to function and cannot be disabled.',
                required: true,
                enabled: true
            },
            analytics: {
                name: 'Analytics Cookies',
                description: 'Help us understand how visitors interact with our website',
                details: 'We use Google Analytics to collect anonymous usage data to improve our service.',
                required: false,
                enabled: true // Changed to true by default
            },
            marketing: {
                name: 'Marketing Cookies',
                description: 'Used to deliver personalized advertisements',
                details: 'These cookies track your activity to show relevant ads on other websites.',
                required: false,
                enabled: true // Changed to true by default
            }
        };
    }

    /**
     * Check if running in development environment
     */
    checkIfDevelopment(): boolean {
        // Check for common development indicators
        const hostname = window.location.hostname;
        const protocol = window.location.protocol;
        const port = window.location.port;
        
        // Development indicators:
        // - localhost or 127.0.0.1
        // - file:// protocol (local file access)
        // - http protocol (not https) on non-production domains
        // - non-standard ports (8000, 3000, 5000, etc.)
        // - NOT easypic.myspeedtools.com (production domain)
        const isProductionDomain = hostname === 'easypic.myspeedtools.com';
        const isDev = !isProductionDomain && (
            hostname === 'localhost' ||
            hostname === '127.0.0.1' ||
            hostname === '0.0.0.0' ||
            hostname === '' || // file:// protocol has empty hostname
            protocol === 'file:' ||
            (protocol === 'http:' && hostname !== 'easypic.myspeedtools.com') ||
            (port !== '' && port !== '80' && port !== '443')
        );
        
        console.log(`Environment detected: ${isDev ? 'Development' : 'Production'} (${protocol}//${hostname}${port ? ':' + port : ''})`);
        return isDev;
    }

    /**
     * Get stored cookie consent preferences
     * Read from localStorage for persistence in both dev and production
     */
    getCookieConsent(): CookieConsent {
        // Read from localStorage for persistence
        try {
            const stored = localStorage.getItem('cookieConsent');
            if (stored) {
                const consent = JSON.parse(stored) as CookieConsent;
                console.log(`${this.isDevelopment ? 'DEVELOPMENT' : 'PRODUCTION'} MODE: Reading stored cookie consent`);
                return consent;
            }
        } catch (error) {
            console.error('Error reading cookie consent:', error);
        }
        
        // Default state (first visit)
        console.log(`${this.isDevelopment ? 'DEVELOPMENT' : 'PRODUCTION'} MODE: No stored consent found, showing modal`);
        return {
            hasConsented: false,
            essential: true,
            analytics: true, // Changed to true by default
            marketing: true, // Changed to true by default
            timestamp: null
        };
    }

    /**
     * Save cookie consent preferences
     * Save to localStorage for persistence in both dev and production
     */
    saveCookieConsent(preferences: { analytics: boolean; marketing: boolean }): void {
        const consent: CookieConsent = {
            hasConsented: true,
            essential: true, // Always true
            analytics: preferences.analytics || false,
            marketing: preferences.marketing || false,
            timestamp: Date.now()
        };

        // Save to localStorage
        try {
            localStorage.setItem('cookieConsent', JSON.stringify(consent));
            console.log(`${this.isDevelopment ? 'DEVELOPMENT' : 'PRODUCTION'} MODE: Cookie consent saved:`, consent);
        } catch (error) {
            console.error('Error saving cookie consent:', error);
        }

        // Always update internal state and handle consent change
        this.cookieConsent = consent;
        this.updateCookieTypes();
        this.handleConsentChange(consent);
    }

    /**
     * Update cookie types with current preferences
     */
    updateCookieTypes(): void {
        this.cookieTypes.analytics.enabled = this.cookieConsent.analytics;
        this.cookieTypes.marketing.enabled = this.cookieConsent.marketing;
    }

    /**
     * Accept all cookies
     */
    acceptAll(): void {
        this.saveCookieConsent({
            analytics: true,
            marketing: true
        });
    }

    /**
     * Reject all non-essential cookies
     */
    rejectAll(): void {
        this.saveCookieConsent({
            analytics: false,
            marketing: false
        });
    }

    /**
     * Save custom cookie settings
     */
    saveSettings(settings: { analytics: boolean; marketing: boolean }): void {
        this.saveCookieConsent(settings);
    }

    /**
     * Check if user has given consent
     * Return actual consent status
     */
    hasConsented(): boolean {
        return this.cookieConsent.hasConsented;
    }

    /**
     * Check if specific cookie type is enabled
     */
    isCookieTypeEnabled(type: keyof Omit<CookieConsent, 'hasConsented' | 'timestamp'>): boolean {
        return this.cookieConsent[type] || false;
    }

    /**
     * Handle consent change - implement actual cookie logic here
     */
    handleConsentChange(consent: CookieConsent): void {
        // TODO: Implement actual cookie management logic
        console.log('Cookie consent updated:', consent);

        // Example: Enable/disable Google Analytics
        if (consent.analytics) {
            this.enableAnalytics();
        } else {
            this.disableAnalytics();
        }

        // Example: Enable/disable marketing cookies
        if (consent.marketing) {
            this.enableMarketing();
        } else {
            this.disableMarketing();
        }

        // Dispatch custom event for other modules to listen to
        window.dispatchEvent(new CustomEvent('cookieConsentChanged', {
            detail: consent
        }));
    }

    /**
     * Enable Google Analytics
     * TODO: Implement actual analytics enablement
     */
    enableAnalytics(): void {
        // Placeholder for analytics enablement logic
        console.log('Analytics cookies enabled');
        
        // Example: Re-initialize Google Analytics if it exists
        if (typeof (window as any).gtag !== 'undefined') {
            (window as any).gtag('consent', 'update', {
                'analytics_storage': 'granted'
            });
        }
    }

    /**
     * Disable Google Analytics
     * TODO: Implement actual analytics disablement
     */
    disableAnalytics(): void {
        // Placeholder for analytics disablement logic
        console.log('Analytics cookies disabled');
        
        // Example: Update Google Analytics consent
        if (typeof (window as any).gtag !== 'undefined') {
            (window as any).gtag('consent', 'update', {
                'analytics_storage': 'denied'
            });
        }
    }

    /**
     * Enable marketing cookies
     * TODO: Implement actual marketing cookie logic
     */
    enableMarketing(): void {
        // Placeholder for marketing cookie enablement logic
        console.log('Marketing cookies enabled');
        
        // Example: Enable advertising storage
        if (typeof (window as any).gtag !== 'undefined') {
            (window as any).gtag('consent', 'update', {
                'ad_storage': 'granted'
            });
        }
    }

    /**
     * Disable marketing cookies
     * TODO: Implement actual marketing cookie disablement
     */
    disableMarketing(): void {
        // Placeholder for marketing cookie disablement logic
        console.log('Marketing cookies disabled');
        
        // Example: Disable advertising storage
        if (typeof (window as any).gtag !== 'undefined') {
            (window as any).gtag('consent', 'update', {
                'ad_storage': 'denied'
            });
        }
    }

    /**
     * Get cookie types for display in settings
     */
    getCookieTypes(): CookieTypes {
        return this.cookieTypes;
    }
}
